import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";

export interface FinanceTransaction {
  id?: string;
  sf_transaction_id?: string;
  transaction_id?: string;
  transaction_number?: string;
  allocation_id?: string;
  invoice_id?: string;
  invoice_name?: string;
  sf_invoice_number?: string;
  owner_name?: string;
  account_id?: string;
  account_name?: string;
  ewallet_id?: string;
  ewallet_name?: string;
  debit_account?: string;
  credit_account?: string;
  ccy?: string;
  currency?: string;
  amount?: number;
  effective_date?: string;
  status?: string;
  type?: string;
  trustee?: string;
  notes?: string;
  created_at?: string;
  related_transaction?: FinanceTransaction | FinanceTransaction[] | null;
  related_transactions?: FinanceTransaction[];
}

export interface FinanceEwalletBankAccount {
  id?: string;
  name?: string;
  bank_name?: string;
  bank_account_number?: string;
  beneficiary_name?: string;
  currency?: string;
}

export interface FinanceEwallet {
  id?: string;
  ewallet_id?: string;
  sf_ewallet_id?: string;
  owner_id?: string;
  owner_name?: string;
  owner_type?: string;
  type?: string;
  name?: string;
  internal_name?: string;
  ccy?: string;
  currency?: string;
  balance?: number;
  total_balance?: number;
  available_balance?: number;
  e_wallet_trustee_name?: string;
  ewallet_trustee_name?: string;
  trustee_name?: string;
  e_wallet_trustee?: string;
  ewallet_trustee?: string;
  trustee?: string;
  bank_account?: FinanceEwalletBankAccount | string | null;
  bank_accounts?: FinanceEwalletBankAccount[];
}

export interface FinanceOwnerOption {
  id: string;
  name: string;
  internal_name?: string;
  owner_type?: string;
  sf_account_id?: string;
  sf_ewallet_id?: string;
}

const normalizeFinanceOwnerOption = (row: any): FinanceOwnerOption | null => {
  if (!row) {
    return null;
  }
  if (typeof row === "string") {
    return { id: row, name: row };
  }
  const id = String(
    row.id ??
      row.owner_id ??
      row.account_id ??
      row.funder_id ??
      row.agent_id ??
      row.partner_id ??
      row.internal_name ??
      row.name ??
      "",
  ).trim();
  const name = String(
    row.name ??
      row.owner_name ??
      row.account_name ??
      row.funder_name ??
      row.agent_name ??
      row.partner_name ??
      row.internal_name ??
      id,
  ).trim();

  if (!id || !name) {
    return null;
  }

  return {
    id,
    name,
    internal_name:
      typeof row.internal_name === "string" ? row.internal_name : undefined,
    owner_type: typeof row.owner_type === "string" ? row.owner_type : undefined,
    sf_account_id:
      typeof row.sf_account_id === "string" ? row.sf_account_id : undefined,
    sf_ewallet_id:
      typeof row.sf_ewallet_id === "string" ? row.sf_ewallet_id : undefined,
  };
};

export interface FinanceTransactionsParams {
  page?: number;
  per_page?: number;
  allocation_id?: string;
  invoice_id?: string;
  account_id?: string;
  ewallet_id?: string;
  currency?: string;
}

export interface FinanceEwalletsParams {
  page?: number;
  per_page?: number;
  owner_type?: string;
  owner_id?: string;
}

export interface FinanceTransactionsResponse {
  transactions: FinanceTransaction[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

export interface FinanceEwalletsResponse {
  ewallets: FinanceEwallet[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

export interface DoubleEntryTransactionPayload {
  invoice_id?: string;
  sf_invoice_id?: string;
  debit_account: string;
  from_sf_account_id?: string;
  credit_account: string;
  to_sf_account_id?: string;
  ewallet_id?: string;
  sf_ewallet_id?: string;
  from_sf_ewallet_id?: string;
  to_sf_ewallet_id?: string;
  debit_ewallet_id?: string;
  credit_ewallet_id?: string;
  currency: string;
  amount: number;
  effective_date: string;
  status?: string;
  type?: string;
  trustee: string;
  notes?: string;
}

export interface SingleTransactionPayload {
  invoice_id?: string;
  sf_invoice_id?: string;
  account_id: string;
  sf_account_id?: string;
  ewallet_id?: string;
  sf_ewallet_id?: string;
  currency: string;
  amount: number;
  effective_date: string;
  status?: string;
  type?: string;
  trustee: string;
  notes?: string;
}

const normalizePagination = (
  pagination: any,
  fallbackTotal: number,
  fallbackPerPage: number,
) => ({
  current_page: pagination.current_page ?? pagination.page ?? 1,
  per_page: pagination.per_page ?? pagination.perPage ?? fallbackPerPage,
  total: pagination.total ?? fallbackTotal,
  last_page:
    pagination.last_page ??
    pagination.lastPage ??
    Math.max(
      1,
      Math.ceil(
        (pagination.total ?? fallbackTotal) /
          (pagination.per_page ?? pagination.perPage ?? fallbackPerPage),
      ),
    ),
});

export const finance = createApi({
  reducerPath: "finance",
  tagTypes: ["BALANCES", "FINANCE_TRANSACTIONS"],
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}/finance`,
  }),
  endpoints: (builder) => ({
    getBalances: builder.query<
      {
        amount: number;
        ccy: string;
        created_date: Date;
        effective_date: string;
        fx_usd_rate_daily_amount: number;
        invoice_finance_deal_name: string;
        invoice_finance_deal_url: string;
        posting_id: string;
        running_balance: number;
        running_balance_usd: number;
        status: string;
        transaction_id: string;
        transaction_url: string;
        trustee: string;
        type: string;
      }[],
      string
    >({
      query: (ewallet_name) => ({
        url: `/get-running-balances-from-ewallet?ewallet_name=${ewallet_name}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["BALANCES"],
    }),
    getFinanceTransactions: builder.query<
      FinanceTransactionsResponse,
      FinanceTransactionsParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        queryParams.append("page", String(params?.page ?? 1));
        queryParams.append("per_page", String(params?.per_page ?? 10));
        if (params?.allocation_id) {
          queryParams.append("allocation_id", params.allocation_id);
        }
        if (params?.invoice_id) {
          queryParams.append("invoice_id", params.invoice_id);
        }
        if (params?.account_id) {
          queryParams.append("account_id", params.account_id);
        }
        if (params?.ewallet_id) {
          queryParams.append("ewallet_id", params.ewallet_id);
        }
        if (params?.currency) {
          queryParams.append("currency", params.currency);
        }
        return {
          url: `/transactions?${queryParams.toString()}`,
          method: "get",
        };
      },
      transformResponse: (response: any): FinanceTransactionsResponse => {
        const data = response?.data ?? response;
        const transactions = data?.transactions ?? data?.data ?? [];
        const pagination = data?.pagination ?? data?.meta ?? {};
        return {
          transactions: Array.isArray(transactions) ? transactions : [],
          pagination: normalizePagination(
            pagination,
            data?.total ?? transactions.length ?? 0,
            data?.per_page ?? 10,
          ),
        };
      },
      providesTags: ["FINANCE_TRANSACTIONS"],
    }),
    getFinanceEwallets: builder.query<
      FinanceEwalletsResponse,
      FinanceEwalletsParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        queryParams.append("page", String(params?.page ?? 1));
        queryParams.append("per_page", String(params?.per_page ?? 10));
        if (params?.owner_type) {
          queryParams.append("owner_type", params.owner_type);
        }
        if (params?.owner_id) {
          queryParams.append("owner_id", params.owner_id);
        }
        return {
          url: `/ewallets?${queryParams.toString()}`,
          method: "get",
        };
      },
      transformResponse: (response: any): FinanceEwalletsResponse => {
        const data = response?.data ?? response;
        const ewallets = data?.ewallets ?? data?.data ?? data?.rows ?? [];
        const pagination = data?.pagination ?? data?.meta ?? {};
        return {
          ewallets: Array.isArray(ewallets) ? ewallets : [],
          pagination: normalizePagination(
            pagination,
            data?.total ?? ewallets.length ?? 0,
            data?.per_page ?? 10,
          ),
        };
      },
    }),
    getFinanceEwallet: builder.query<FinanceEwallet, string>({
      query: (ewalletId) => ({
        url: `/ewallets/${ewalletId}`,
        method: "get",
      }),
      transformResponse: (response: any): FinanceEwallet =>
        response?.data ?? response,
    }),
    searchFinanceEwalletOwners: builder.query<
      FinanceOwnerOption[],
      { owner_type: string; search: string }
    >({
      query: ({ owner_type, search }) => {
        const queryParams = new URLSearchParams();
        queryParams.append("owner_type", owner_type);
        queryParams.append("search", search);
        return {
          url: `/ewallets/owners/search?${queryParams.toString()}`,
          method: "get",
        };
      },
      transformResponse: (response: any): FinanceOwnerOption[] => {
        const data = response?.data ?? response;
        const rows = data?.owners ?? data?.data ?? data?.rows ?? data ?? [];
        if (!Array.isArray(rows)) {
          return [];
        }
        return rows
          .map((row) => normalizeFinanceOwnerOption(row))
          .filter(Boolean) as FinanceOwnerOption[];
      },
    }),
    getFinanceTransactionsByEwallet: builder.query<
      FinanceTransactionsResponse,
      { ewallet_id: string; page?: number; per_page?: number }
    >({
      query: ({ ewallet_id, page = 1, per_page = 10 }) => {
        const queryParams = new URLSearchParams();
        queryParams.append("ewallet_id", ewallet_id);
        queryParams.append("page", String(page));
        queryParams.append("per_page", String(per_page));
        return {
          url: `/transactions?${queryParams.toString()}`,
          method: "get",
        };
      },
      transformResponse: (response: any): FinanceTransactionsResponse => {
        const data = response?.data ?? response;
        const transactions = data?.transactions ?? data?.data ?? [];
        const pagination = data?.pagination ?? data?.meta ?? {};
        return {
          transactions: Array.isArray(transactions) ? transactions : [],
          pagination: normalizePagination(
            pagination,
            data?.total ?? transactions.length ?? 0,
            data?.per_page ?? 10,
          ),
        };
      },
    }),
    getFinanceTransaction: builder.query<FinanceTransaction, string>({
      query: (transactionId) => ({
        url: `/transactions/${transactionId}`,
        method: "get",
      }),
      transformResponse: (response: any) => response?.data ?? response,
      providesTags: (_result, _error, transactionId) => [
        { type: "FINANCE_TRANSACTIONS", id: transactionId },
      ],
    }),
    createDoubleEntryTransaction: builder.mutation<
      FinanceTransaction,
      DoubleEntryTransactionPayload
    >({
      query: (data) => ({
        url: "/transactions/double-entry",
        method: "post",
        data,
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: ["FINANCE_TRANSACTIONS", "BALANCES"],
    }),
    createFinanceTransaction: builder.mutation<
      FinanceTransaction,
      SingleTransactionPayload
    >({
      query: (data) => ({
        url: "/transactions",
        method: "post",
        data,
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: ["FINANCE_TRANSACTIONS", "BALANCES"],
    }),
    updateFinanceTransaction: builder.mutation<
      FinanceTransaction,
      {
        transactionId: string;
        data: Partial<DoubleEntryTransactionPayload & SingleTransactionPayload>;
      }
    >({
      query: ({ transactionId, data }) => ({
        url: `/transactions/${transactionId}`,
        method: "patch",
        data,
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: (_result, _error, { transactionId }) => [
        "FINANCE_TRANSACTIONS",
        { type: "FINANCE_TRANSACTIONS", id: transactionId },
        "BALANCES",
      ],
    }),
  }),
});

export const {
  useGetBalancesQuery,
  useGetFinanceEwalletsQuery,
  useGetFinanceEwalletQuery,
  useGetFinanceTransactionsQuery,
  useGetFinanceTransactionsByEwalletQuery,
  useGetFinanceTransactionQuery,
  useLazyGetFinanceTransactionsByEwalletQuery,
  useSearchFinanceEwalletOwnersQuery,
  useCreateFinanceTransactionMutation,
  useCreateDoubleEntryTransactionMutation,
  useUpdateFinanceTransactionMutation,
} = finance;
